import { ArrowLeft } from "lucide-react";
import React, { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Link, useNavigate, useParams } from "react-router";
import { RequireScope } from "src/components/RequireScope";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { MarkdownEditorLazy } from "src/components/ui/markdown-editor-lazy";
import { useMe } from "src/context/MeContext";
import { useToast } from "src/hooks/use-toast";
import { getKnowledgeFileName, getProductName } from "src/utils/brandTheme";

import { useSettingsMobileHeader } from "../hooks/useSettingsMobileHeader";
import { EditTeamRouteQuery } from "./__generated__/EditTeamRouteQuery.graphql";
import { EditTeamRouteUpdateTeamMutation } from "./__generated__/EditTeamRouteUpdateTeamMutation.graphql";

const editTeamRouteQuery = graphql`
  query EditTeamRouteQuery($teamId: GlobalID!) {
    node(id: $teamId) {
      ... on GraphQLTeam {
        id
        name
        description
      }
    }
  }
`;

const updateTeamMutation = graphql`
  mutation EditTeamRouteUpdateTeamMutation($input: UpdateTeamInput!) {
    updateTeam(input: $input) {
      team {
        id
        name
        description
      }
    }
  }
`;

export default function EditTeamRoute() {
  return (
    <RequireScope scope="write:teams">
      <EditTeamRouteContent />
    </RequireScope>
  );
}

function EditTeamRouteContent() {
  useSettingsMobileHeader("Edit Team");
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const { toast } = useToast();
  const me = useMe();

  const data = useLazyLoadQuery<EditTeamRouteQuery>(editTeamRouteQuery, {
    teamId: teamId || "",
  });

  const [commitUpdateTeam, isUpdating] = useMutation<EditTeamRouteUpdateTeamMutation>(updateTeamMutation);

  const team = data.node;

  const [teamName, setTeamName] = useState(team?.name || "");
  const [teamDescription, setTeamDescription] = useState(team?.description || "");

  const brandTheme = me.organization?.brandTheme ?? "TIERZERO";
  const productName = getProductName(brandTheme);

  const handleUpdateTeam = () => {
    if (!team?.id || !teamName.trim()) {
      toast({
        title: "Error",
        description: "Team name is required",
        variant: "error",
      });
      return;
    }

    commitUpdateTeam({
      variables: {
        input: {
          id: team.id,
          name: teamName.trim(),
          description: teamDescription.trim() || null,
        },
      },
      onCompleted: () => {
        toast({
          title: "Success",
          description: "Team updated successfully",
        });
        navigate("/settings/teams");
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "error",
        });
      },
    });
  };

  if (!team) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <LoadingSpinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="flex-1 px-4 md:px-5 pt-4 pb-5">
      {/* Header with back button */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate("/settings/teams")}
          className="p-2 hover:bg-accent rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-muted-foreground" />
        </button>
        <h1 className="text-xl font-semibold text-foreground">Edit Team</h1>
      </div>

      <div className="max-w-4xl space-y-6">
        {/* Basic Info Section */}
        <div className="bg-card/30 rounded-lg p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="team-name">Team Name <span className="text-red-500">*</span></Label>
            <Input
              id="team-name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Enter team name"
              className="max-w-md"
            />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <p className="text-muted-foreground text-sm">
              Describe what this team handles. {productName} uses this to decide when to route requests to this team.
            </p>
            <div className="max-w-2xl">
              <MarkdownEditorLazy
                value={teamDescription}
                onChange={setTeamDescription}
                placeholder="E.g., Handles payment processing, billing issues, and subscription management"
                minHeight="360px"
                autoFocus={false}
              />
            </div>
          </div>
        </div>

        {/* Team Instructions Section */}
        <div className="bg-card/30 rounded-lg p-6 space-y-4">
          <div>
            <Label>Team Instructions</Label>
            <p className="text-muted-foreground text-sm mt-1">
              Please go to{" "}
              <Link
                to={`/knowledge/${getKnowledgeFileName(brandTheme).toLowerCase()}?team=${team.id}`}
                className="text-primary hover:text-primary/80 hover:underline"
              >
                {getKnowledgeFileName(brandTheme)}
              </Link>
              {" "}to modify team-specific context and instructions that aren't in the global instructions file.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => navigate("/settings/teams")}
            disabled={isUpdating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateTeam}
            disabled={isUpdating || !teamName.trim()}
          >
            {isUpdating ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
